import { rem } from 'polished'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import Group from 'components/utils/Group'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import { useAppSelector } from 'hooks/reduxHooks'
import Divider from 'LuxKit/Divider'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import TextLink from 'LuxKit/TextLink'
import Tooltip from 'LuxKit/Tooltip'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { LOYALTY_PURCHASE_POINTS_PAGE } from 'loyalty/constants/loyaltyRoutes'
import LoyaltySwitchMembershipRegionPanel from 'loyalty/components/LoyaltySwitchMembershipRegionPanel'
import useCurrentLoyaltyPendingPoints from 'loyalty/hooks/useCurrentLoyaltyPendingPoints'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import getIsCurrentRegionUserLoyaltyRegion from 'loyalty/selectors/getIsCurrentRegionUserLoyaltyRegion'
import getLoyaltyAccountRegion from 'loyalty/selectors/getLoyaltyAccountRegion'
import { tierGradientSecondaryBackgroundStyle, tierNormalContrastColorStyle } from 'styles/utils/tierStyles'

const PointsBalanceCard = styled.div`
  width: 100%;
  max-width: ${rem(383)};
  overflow: hidden;
  border-radius: ${props => props.theme.borderRadius.L};
  box-shadow: ${props => props.theme.shadow.bottom.medium};
`

const PointsBalanceCardHeader = styled.div`
  padding: ${rem(16)} ${rem(24)};
  text-align: center;
  ${tierGradientSecondaryBackgroundStyle}
  ${tierNormalContrastColorStyle}
`

const PointsBalanceCardBody = styled.div`
  padding: ${rem(16)} ${rem(24)};
  color: ${props => props.theme.palette.neutral.default.one};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const InfoIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  line-height: 1;
`

interface Props {
  points: number
  programName: string
  userTier: App.LoyaltyTier
}

function LoyaltyPointsBalanceCard({ points, programName, userTier }: Props) {
  const intl = useIntl()
  const [pendingPoints] = useCurrentLoyaltyPendingPoints()
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const isInLoyaltyRegion = useAppSelector(getIsCurrentRegionUserLoyaltyRegion)
  const loyaltyAccountRegion = useAppSelector(getLoyaltyAccountRegion)
  const showWrongRegionState = hasLoyaltyAccount && !isInLoyaltyRegion && !!loyaltyAccountRegion

  return <ProductPaletteProvider palette="default">
    <PointsBalanceCard>
      <PointsBalanceCardHeader className={`tier-${userTier}`}>
        <Heading variant="heading6" as="h6">
          {intl.formatMessage({
            id: '_2ZruFZvpCPNtPsmYCsA',
            defaultMessage: 'Your points balance',
            description: 'Heading on the points balance card in the Infinite member account hero',
          })}
        </Heading>
      </PointsBalanceCardHeader>
      <PointsBalanceCardBody>
        {showWrongRegionState ? <LoyaltySwitchMembershipRegionPanel programName={programName} userTier={userTier} /> : <Group direction="vertical" gap={12} horizontalAlign="center">
          <Group direction="vertical" gap={4} horizontalAlign="center">
            <Group direction="horizontal" gap={4} verticalAlign="baseline" horizontalAlign="center">
              <Heading variant="heading2"><FormatDecimal value={points} /></Heading>
              <BodyText variant="medium" weight="bold">
                {intl.formatMessage({
                  id: 'DLJIxrZ4JoUzmTiyZSnk',
                  defaultMessage: 'pts',
                  description: 'Abbreviated points unit label displayed in the loyalty account menu tile',
                })}
              </BodyText>
            </Group>
            <Group direction="horizontal" gap={4} verticalAlign="center" horizontalAlign="center">
              <BodyText variant="medium">
                <FormattedMessage
                  id="E5yjZp88aViZnM9Sfuqo"
                  defaultMessage="{count} pending"
                  description="Count of pending loyalty points that have not yet been credited to the account"
                  values={{ count: <FormatDecimal value={pendingPoints.points} /> }}
                />
              </BodyText>
              <Tooltip
                description={intl.formatMessage({
                  id: 'MNjBpmX8tq42ZH7nJ7fk',
                  defaultMessage: 'Pending points may take up to 24hrs to appear',
                  description: 'Tooltip explaining pending points delay in the loyalty program section',
                })}
                placement="bottom"
              >
                <InfoIconWrapper><LineInfoCircleIcon size={16} /></InfoIconWrapper>
              </Tooltip>
            </Group>
          </Group>
          <Divider kind="secondary" />
          <BodyText variant="medium">
            <FormattedMessage
              id="dYDrBoYWfRUvMxtXmbP4"
              defaultMessage="Need more {programName} Points? <link>Top up now</link>"
              description="Prompt on the spend points page balance card linking to the top up points page"
              values={{
                programName,
                link: chunks => <TextLink size="medium" weight="regular" underline to={`/${LOYALTY_PURCHASE_POINTS_PAGE}`}>{chunks}</TextLink>,
              }}
            />
          </BodyText>
        </Group>}
      </PointsBalanceCardBody>
    </PointsBalanceCard>
  </ProductPaletteProvider>
}

export default LoyaltyPointsBalanceCard
